import { useEffect, useRef, useState } from "react";
import { configAgentError, forexApi, type ConfigTemplateV1 } from "@/lib/forex-api";
import { templateLayoutLabel } from "./strategy-timeframe-layout";

export function ConfigTemplateVersionSelect({ template, onChange, disabled = false }: {
  template: ConfigTemplateV1; onChange: (item: ConfigTemplateV1 | null) => void; disabled?: boolean;
}) {
  const [versions, setVersions] = useState<number[]>([template.template_ref.version]);
  const [next, setNext] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [requested, setRequested] = useState(template.template_ref.version);
  const [busy, setBusy] = useState(false);
  const sequence = useRef({ value: 0 });
  const id = template.template_ref.id;
  useEffect(() => {
    let active = true;
    const gate = sequence.current;
    setVersions([template.template_ref.version]); setNext(null); setError("");
    void forexApi.listConfigTemplateRevisions(id).then((data) => {
      if (active) { setVersions(data.items.map((item) => item.template_ref.version)); setNext(data.next_before_revision); }
    }).catch((cause) => { if (active) setError(configAgentError(cause, "版本读取失败")); });
    return () => { active = false; gate.value++; };
  }, [id, template.template_ref.version]);
  const choose = async (version: number) => {
    const request = ++sequence.current.value;
    setRequested(version); setBusy(true); setError(""); onChange(null);
    try {
      const item = await forexApi.getConfigTemplateRevision(id, version);
      if (request === sequence.current.value) onChange(item);
    } catch (cause) { if (request === sequence.current.value) setError(configAgentError(cause, "版本内容读取失败，请重选")); }
    finally { if (request === sequence.current.value) setBusy(false); }
  };
  const more = async () => {
    if (next === null) return;
    setBusy(true);
    try {
      const data = await forexApi.listConfigTemplateRevisions(id, next);
      setVersions((current) => [...new Set([...current, ...data.items.map((item) => item.template_ref.version)])]);
      setNext(data.next_before_revision); setError("");
    } catch (cause) { setError(configAgentError(cause, "版本读取失败")); }
    finally { setBusy(false); }
  };
  const options = [...new Set([template.template_ref.version, ...versions])].sort((a, b) => b - a);
  return <div className="mt-2 text-xs">
    <label>版本<select aria-label="策略版本" className="ml-2 rounded border p-1"
      disabled={disabled || busy} value={template.template_ref.version} onChange={(event) => void choose(Number(event.target.value))}>
      {options.map((version) => <option key={version} value={version}>v{version}{version === (template.current_revision ?? template.template_ref.version) ? "（最新）" : ""}</option>)}
    </select></label>
    {next !== null && <button type="button" disabled={busy || disabled} onClick={() => void more()} className="ml-2 text-brand">更早版本</button>}
    {busy && <span className="ml-2">读取中</span>}{error && <p role="alert" className="text-red-600">{error}<button type="button" disabled={busy || disabled} onClick={() => void choose(requested)} className="ml-2 underline">重试</button></p>}
  </div>;
}

export function ConfigTemplateHistory({ template, onEdit, disabled }: {
  template: ConfigTemplateV1; onEdit: () => void; disabled: boolean;
}) {
  const [view, setView] = useState<ConfigTemplateV1>(template);
  const [open, setOpen] = useState(false);
  return <div className="mt-3 text-xs">
    <button type="button" disabled={disabled || template.retained_version} onClick={onEdit} className="text-brand disabled:opacity-50">编辑</button>
    <button type="button" onClick={() => setOpen(!open)} className="ml-4 text-brand">{open ? "收起历史版本" : "历史版本"}</button>
    {open && <div className="mt-2">
      <ConfigTemplateVersionSelect template={view} onChange={(item) => { if (item) setView(item); }} />
      <p className="mt-2 rounded bg-surface p-2 text-text-secondary">{String(view.payload.category || "策略")} · {(view.payload.symbols as string[] || []).join("、")} · {templateLayoutLabel(view.payload)} · {String(view.payload.cadence_profile_id || "运行周期待核对")}</p>
    </div>}
  </div>;
}
